import React, { useState, useEffect } from 'react'
import { Box, Paper, Text, Divider, SimpleGrid, Select, TextInput, Flex, Button } from '@mantine/core'
import { getAllCountryList, getAllProvinceList, getAllCityList } from '../../../../../services/location'
import { useUserAdditionalInfoForm } from './schema/form'
import { useSelector, useDispatch } from 'react-redux'
import { updateUser } from '../../../../../services/users'
import { notificationError, notificationSuccess } from '../../../../ui/Notifications'
import { getUserDetail } from '../../../../../store/userDetail'
import { useParams } from 'react-router-dom'

const UserAdditionalInfo = () => {
  const params = useParams()
  const userId = params.id

  const dispatch = useDispatch()
  const { additionalInfo, taxInfo } = useSelector((state) => state.user_detail)

  const [loadingForm, setLoadingForm] = useState(false)
  const [countryList, setCountryList] = useState([])
  const [provinceList, setProvinceList] = useState([])
  const [cityList, setCityList] = useState([])

  const form = useUserAdditionalInfoForm()

  const handleGetCountryList = async () => {
    const params = {
      'page[num]': '1',
      'page[limit]': '99999',
    }
    try {
      const response = await getAllCountryList(params)
      const dataCountry = response.data
      const remapDataCountry = dataCountry.map((val) => {
        return {
          label: val.attributes.name,
          value: `${val.attributes.id}`
        }
      })
      setCountryList(remapDataCountry)

    } catch (error) {
      console.log(error)
    }
  }

  const handleGetProvinceList = async (countryId) => {
    const params = {
      'page[num]': '1',
      'page[limit]': '99999',
      'filter[countries.id][is]': countryId,
      sort: '-id',
    }
    try {
      const response = await getAllProvinceList(params)
      const dataProvince = response.data
      const remapDataProvince = dataProvince.map((val) => {
        return {
          label: val.attributes.name,
          value: `${val.attributes.id}`
        }
      })
      setProvinceList(remapDataProvince)
    } catch (error) {
      console.log(error)
    }
  }

  const handleGetCityList = async (countryId, provinceId) => {
    const params = {
      'page[num]': '1',
      'page[limit]': '99999',
      'filter[countries.id][is]': countryId,
      'filter[provinces.id][is]': provinceId,
      sort: '-id',
    }
    try {
      const response = await getAllCityList(params)
      const dataCity = response.data
      const remapDataCity = dataCity.map((val) => {
        return {
          label: val.attributes.name,
          value: `${val.attributes.id}`
        }
      })
      setCityList(remapDataCity)
    } catch (error) {
      console.log(error)
    }
  }

  const handleSetForm = (data) => {
    const infoAddress = data.addressInformation[0]
    const infoWithdraw = data.withdrawal

    const formData = {
      country_id: infoAddress && infoAddress.country_id ? `${infoAddress.country_id}` : null,
      province_id: infoAddress && infoAddress.province_id ? `${infoAddress.province_id}` : null,
      city_id: infoAddress && infoAddress.city_id ? `${infoAddress.city_id}` : null,
      address_line: infoAddress ? infoAddress.address_line : '',
      subdistrict: infoAddress ? infoAddress.subdistrict : '',
      zip_code: infoAddress ? infoAddress.zip_code : '',
      withdraw_provider: infoWithdraw.bankName || '',
      withdraw_account_name: infoWithdraw.bankAccountName || '',
      withdraw_account_number: infoWithdraw.bankAccountNumber || '',
      withdraw_swift_code: infoWithdraw.bankSwiftCode || '',
    }
    form.initialize(formData)
    form.setValues(formData)
  }

  const handleSubmitAdditionalInfoUser = async (formData) => {
    setLoadingForm(true)

    const infoAddress = additionalInfo.addressInformation[0]
    const payload = {
      addresses: [
        {
          ...formData.addresses[0],
          id: infoAddress.id || null
        }
      ],
      extras: {
        ...formData.extras,
        identity_card: taxInfo.identityCard,
        taxes_id: taxInfo.taxesId,
        taxes_name: taxInfo.taxName,
        taxes_address: taxInfo.taxAddress,
        type_of_bussines: taxInfo.typeOfBusines,
      }
    }

    try {
      const response =await updateUser(userId, payload)
      if (response.success) {
        setLoadingForm(false)
        notificationSuccess('Update User Success', 'You have been updated data user')
        dispatch(getUserDetail(userId))
      }
    } catch (error) {
      setLoadingForm(false)
      const errorDescriptionMessage = 'There is an error while updating user'
      notificationError('Update User Error', errorDescriptionMessage)
    }
  }

  const handlingScrolledError = (error) => {
    const errorField = Object.keys(error)[0]
    if (errorField) {
      const inputNode = form.getInputNode(errorField)
      if (inputNode) {
        inputNode.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }

  useEffect(() => {
    handleGetCountryList()
    if (additionalInfo) {
      handleSetForm(additionalInfo)
    }
    // eslint-disable-next-line
  }, [additionalInfo])

  useEffect(() => {
    const selectedCountry = form.getValues().country_id
    const selectedProvince = form.getValues().province_id
    if (selectedCountry) {
      handleGetProvinceList(selectedCountry)
    }
    if (selectedProvince) {
      handleGetCityList(selectedCountry, selectedProvince)
    }
    // eslint-disable-next-line
  }, [form.getValues().country_id, form.getValues().province_id])

  form.watch('country_id', ({ previousValue, value }) => {
    if (previousValue !== value) {
      form.setValues((prev) => ({ ...prev, province_id: null, city_id: null }))
      setProvinceList([])
      setCityList([])
    }
    if (value === null) {
      setProvinceList([])
      setCityList([])
      form.setValues((prev) => ({ ...prev, province_id: null, city_id: null }))
    }
  })

  form.watch('province_id', ({ previousValue, value }) => {
    if (previousValue !== value) {
      form.setValues((prev) => ({ ...prev, city_id: null }))
      setCityList([])
    }
    if (value === null) {
      setCityList([])
      form.setValues((prev) => ({ ...prev, city_id: null }))
    }
  })


  return (
    <Box>
      <Paper my='xl' p='md' withBorder shadow='xs' radius='md'>
        <Text fw={500}>
          Address Information
        </Text>
        <Divider my='xs' />

        <SimpleGrid cols={3} verticalSpacing="sm" mb='md'>
          <Box>
            <Select
              name='country_id'
              label='Country'
              placeholder='Select Country'
              data={countryList}
              key={form.key('country_id')}
              {...form.getInputProps('country_id')}
              size='xs'
              searchable
              nothingFoundMessage="No Country Found"
              clearable
              withAsterisk
              allowDeselect={false}
              onClear={() => form.setValues((prev) => ({ ...prev, province_id: null, city_id: null}))}
            />
          </Box>
          <Box>
            <Select
              name='province_id'
              label='Province'
              placeholder='Select Province'
              data={provinceList}
              key={form.key('province_id')}
              {...form.getInputProps('province_id')}
              size='xs'
              searchable
              nothingFoundMessage="No Province Found"
              clearable
              allowDeselect={false}
              disabled={form.getValues().country_id === null}
              onClear={() => form.setValues((prev) => ({ ...prev, city_id: null}))}
            />
          </Box>
          <Box>
            <Select
              name='city_id'
              label='City'
              placeholder='Select City'
              data={cityList}
              key={form.key('city_id')}
              {...form.getInputProps('city_id')}
              size='xs'
              searchable
              nothingFoundMessage="No City Found"
              clearable
              allowDeselect={false}
              disabled={form.getValues().country_id === null || form.getValues().province_id === null}
            />
          </Box>
          <Box>
            <TextInput
              name='address_line'
              label='Street Address'
              placeholder='Enter Street Address'
              size='xs'
              key={form.key('address_line')}
              {...form.getInputProps('address_line')}
              withAsterisk
            />
          </Box>
          <Box>
            <TextInput
              name='subdistrict'
              label='Subdistrict'
              placeholder='Enter Subdistrict'
              size='xs'
              key={form.key('subdistrict')}
              {...form.getInputProps('subdistrict')}
            />
          </Box>
          <Box>
            <TextInput
              name='zip_code'
              label='Postal / ZIP Code'
              placeholder='Enter Postal / ZIP Code'
              size='xs'
              key={form.key('zip_code')}
              {...form.getInputProps('zip_code')}
            />
          </Box>
        </SimpleGrid>
      </Paper>

      <Paper my='xl' p='md' withBorder shadow='xs' radius='md'>
        <Text fw={500}>
          Withdrawal Information
        </Text>
        <Divider my='xs' />
        <SimpleGrid cols={3} verticalSpacing="sm" mb='md'>
          <Box>
            <TextInput
              name='withdraw_provider'
              label='Bank Name'
              placeholder='Enter Bank Name'
              size='xs'
              key={form.key('withdraw_provider')}
              {...form.getInputProps('withdraw_provider')}
            />
          </Box>
          <Box>
            <TextInput
              name='withdraw_account_name'
              label='Bank Account Name'
              placeholder='Enter Bank Account Name'
              size='xs'
              key={form.key('withdraw_account_name')}
              {...form.getInputProps('withdraw_account_name')}
            />
          </Box>
          <Box>
            <TextInput
              name='withdraw_account_number'
              label='Bank Account Number'
              placeholder='Enter Bank Account Number'
              size='xs'
              key={form.key('withdraw_account_number')}
              {...form.getInputProps('withdraw_account_number')}
            />
          </Box>
          <Box>
            <TextInput
              name='withdraw_swift_code'
              label='Bank Swift Code'
              placeholder='Enter Bank Swift Code'
              size='xs'
              key={form.key('withdraw_swift_code')}
              {...form.getInputProps('withdraw_swift_code')}
            />
          </Box>
        </SimpleGrid>
      </Paper>
      <Box mt='xl'>
        <Flex justify='flex-end'>
          <Button
            size='xs'
            loading={loadingForm}
            variant="filled"
            onClick={form.onSubmit((value) => {
              handleSubmitAdditionalInfoUser(value) 
            }, (error) => {
              handlingScrolledError(error)
            })}
            >
              Update
            </Button>
        </Flex>
      </Box>
    </Box>
  )
}

export default UserAdditionalInfo